import type { MotionAmplificationJobId } from "../clients/motion-amplification";

const DATABASE_NAME = "modality-multipart-upload-handles";
const STORE_NAME = "file-handles";

function openHandleDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE_NAME);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function withHandleStore<T>(
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const database = await openHandleDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const request = operation(
        database.transaction(STORE_NAME, mode).objectStore(STORE_NAME),
      );
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } finally {
    database.close();
  }
}

export async function saveMultipartFileHandle(
  jobId: MotionAmplificationJobId,
  handle: FileSystemFileHandle,
): Promise<void> {
  await withHandleStore("readwrite", (store) => store.put(handle, jobId));
}

export async function getMultipartFileFromHandle(
  jobId: MotionAmplificationJobId,
): Promise<File | null> {
  const handle = await withHandleStore<FileSystemFileHandle | undefined>(
    "readonly",
    (store) => store.get(jobId),
  );
  if (!handle) return null;

  const permissionHandle = handle as FileSystemFileHandle & {
    queryPermission?: (descriptor: {
      mode: "read";
    }) => Promise<PermissionState>;
    requestPermission?: (descriptor: {
      mode: "read";
    }) => Promise<PermissionState>;
  };
  if (!permissionHandle.queryPermission) return null;
  const permission = await permissionHandle.queryPermission({ mode: "read" });
  const granted =
    permission === "granted" ||
    (permission === "prompt" &&
      permissionHandle.requestPermission &&
      (await permissionHandle.requestPermission({ mode: "read" })) ===
        "granted");
  if (!granted) return null;

  try {
    return await handle.getFile();
  } catch {
    return null;
  }
}

export async function removeMultipartFileHandle(
  jobId: MotionAmplificationJobId,
): Promise<void> {
  await withHandleStore("readwrite", (store) => store.delete(jobId));
}
